import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  .sl * { box-sizing:border-box; }
  .sl { font-family:'Sora',sans-serif; color:#1a1d2e; animation:slFade .4s ease both; }
  @keyframes slFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }

  .sl-header { margin-bottom:1.5rem; }
  .sl-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .sl-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.2rem; width:fit-content; max-width:100%; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; position:relative; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .tab-count { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.62rem; font-weight:700; margin-left:6px; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }

  .lv-table { width:100%; border-collapse:collapse; }
  .lv-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .lv-table th { padding:10px 16px; text-align:center !important; font-size:.64rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .lv-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .lv-table tbody tr:last-child { border-bottom:none; }
  .lv-table tbody tr:hover { background:#fafbff; }
  .lv-table td { padding:12px 16px; font-size:.8rem; vertical-align:top; text-align:center !important; }

  .stu-name { font-weight:700; font-size:.84rem; color:#0f1123; }
  .stu-meta { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .date-range { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#3b6ef8; font-weight:600; }
  .day-count  { font-size:.68rem; color:#9094a8; margin-top:2px; }
  .reason-text { font-size:.78rem; color:#4a5075; max-width:260px; line-height:1.4; }

  .status-badge { display:inline-flex; align-items:center; gap:4px; font-size:.7rem; font-weight:700; padding:4px 10px; border-radius:99px; white-space:nowrap; }
  .status-badge.pending  { background:#fffbeb; color:#92400e; border:1px solid #fde68a; }
  .status-badge.approved { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .status-badge.rejected { background:#fef2f2; color:#991b1b; border:1px solid #fecaca; }

  .action-btns { display:flex; gap:6px; }
  .act-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 13px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; transition:all .15s; }
  .act-btn.approve { background:#10c98e; color:white; }
  .act-btn.approve:hover { background:#0db87e; }
  .act-btn.reject { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .act-btn.reject:hover { background:#fee2e2; }
  .act-btn:disabled { opacity:.5; cursor:not-allowed; }

  .reviewed-meta { font-size:.7rem; color:#9094a8; }
  .reviewed-meta b { color:#4a5075; }

  .sl-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .sl-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:slSpin .7s linear infinite; }
  @keyframes slSpin { to{transform:rotate(360deg);} }

  /* Mobile cards */
  .lv-table-wrap { overflow-x:auto; }
  .lv-cards { display:none; padding:10px; }
  .lv-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px 14px; margin-bottom:10px; }
  .lv-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .lv-card-row { font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; display:flex; justify-content:space-between; gap:10px; }
  .lv-card-row:last-of-type { border-bottom:none; }
  .lv-card-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .lv-card-actions { margin-top:10px; display:flex; gap:8px; }
  .lv-card-actions .act-btn { flex:1; justify-content:center; }

  /* Reject modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.6); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:18px; width:100%; max-width:420px; padding:22px; box-shadow:0 25px 60px rgba(0,0,0,.3); animation:slMo .2s ease; }
  @keyframes slMo { from{opacity:0;transform:scale(.95)} to{opacity:1;transform:scale(1)} }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; margin-bottom:14px; }
  .mo-textarea { width:100%; font-family:'Sora',sans-serif; font-size:.82rem; padding:10px 12px; border:1.5px solid #e0e4f0; border-radius:10px; outline:none; resize:vertical; min-height:80px; }
  .mo-textarea:focus { border-color:#3b6ef8; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; }
  .btn-ghost { padding:8px 16px; border-radius:9px; border:none; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; }

  @media (max-width:768px) {
    .sl-title { font-size:1.1rem; }
    .lv-table-wrap { display:none !important; }
    .lv-cards { display:block; }
  }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '';
const dayCount = (start,end) => {
  const s = new Date(start); s.setHours(0,0,0,0);
  const e = new Date(end);   e.setHours(0,0,0,0);
  return Math.round((e-s)/86400000)+1;
};

const STATUS_LABEL = { pending:'⏳ Pending', approved:'✓ Approved', rejected:'✗ Rejected' };

export default function TeacherStudentLeaves() {
  const [tab,      setTab]      = useState('all');
  const [leaves,   setLeaves]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [busyId,   setBusyId]   = useState(null);
  const [rejectFor,setRejectFor]= useState(null);
  const [rejectNote,setRejectNote]= useState('');
  const [approveFor,setApproveFor]= useState(null);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const status = tab === 'all' ? '' : tab;
      const { data } = await api.get(`/teacher/student-leaves${status ? `?status=${status}` : ''}`);
      setLeaves(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load student leaves'); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const counts = leaves.reduce((acc,l) => { acc[l.status]=(acc[l.status]||0)+1; return acc; }, {});

  const approve = (leave) => setApproveFor(leave);

  const confirmApprove = async () => {
    if (!approveFor) return;
    const leave = approveFor;
    try {
      setBusyId(leave._id);
      const { data } = await api.put(`/teacher/student-leaves/${leave._id}/review`, { status:'approved' });
      toast.success(data?.message || 'Leave approved');
      setApproveFor(null);
      fetchLeaves();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to approve'); }
    finally { setBusyId(null); }
  };

  const openReject = (leave) => { setRejectFor(leave); setRejectNote(''); };

  const confirmReject = async () => {
    if (!rejectFor) return;
    if (!rejectNote.trim()) { toast.error('Reason is required'); return; }
    try {
      setBusyId(rejectFor._id);
      const { data } = await api.put(`/teacher/student-leaves/${rejectFor._id}/review`, { status:'rejected', reviewNote: rejectNote.trim() });
      toast.success(data?.message || 'Leave rejected');
      setRejectFor(null);
      fetchLeaves();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to reject'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="sl">
      <style>{style}</style>

      <div className="sl-header">
        <div className="sl-title">Student Leaves</div>
        <div className="sl-sub">Review and approve leave requests from students in your classes</div>
      </div>

      <div className="tab-row">
        {['all','approved','pending','rejected'].map(t => (
          <button key={t} className={`tab-btn${tab===t?' active':''}`} onClick={()=>setTab(t)}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
            {t==='pending' && counts.pending>0 && <span className="tab-count">{counts.pending}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="sl-loading"><div className="spinner"/>Loading…</div>
      ) : leaves.length===0 ? (
        <div className="sl-empty">No {tab!=='all'?tab:''} leave requests found.</div>
      ) : (
        <div className="panel">
          <div className="lv-table-wrap">
            <table className="lv-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Dates</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map(l => (
                  <tr key={l._id}>
                    <td>
                      <div className="stu-name">{l.student?.user?.name||'—'}</div>
                      <div className="stu-meta">Roll: {l.student?.rollNo||'—'} · {l.student?.admissionNo||''}</div>
                    </td>
                    <td>{l.class?.name||'—'} {l.student?.section?`— ${l.student.section}`:''}</td>
                    <td>
                      <div className="date-range">{fmtDate(l.start_date)} – {fmtDate(l.end_date)}</div>
                      <div className="day-count">{dayCount(l.start_date,l.end_date)} day(s)</div>
                    </td>
                    <td><div className="reason-text">{l.reason||'—'}</div></td>
                    <td>
                      <span className={`status-badge ${l.status}`}>{STATUS_LABEL[l.status]||l.status}</span>
                      {l.status!=='pending' && l.reviewedBy && (
                        <div className="reviewed-meta" style={{marginTop:5}}>by <b>{l.reviewedBy.name}</b></div>
                      )}
                      {l.review_note && <div className="reviewed-meta" style={{marginTop:2}}>"{l.review_note}"</div>}
                    </td>
                    <td>
                      {l.status==='pending' ? (
                        <div className="action-btns">
                          <button className="act-btn approve" disabled={busyId===l._id} onClick={()=>approve(l)}>✓ Approve</button>
                          <button className="act-btn reject"  disabled={busyId===l._id} onClick={()=>openReject(l)}>✗ Reject</button>
                        </div>
                      ) : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lv-cards">
            {leaves.map(l => (
              <div key={l._id} className="lv-card">
                <div className="lv-card-top">
                  <div>
                    <div className="stu-name">{l.student?.user?.name||'—'}</div>
                    <div className="stu-meta">Roll: {l.student?.rollNo||'—'}</div>
                  </div>
                  <span className={`status-badge ${l.status}`}>{STATUS_LABEL[l.status]||l.status}</span>
                </div>
                <div className="lv-card-row"><span className="lv-card-label">Class</span><span>{l.class?.name||'—'} {l.student?.section?`— ${l.student.section}`:''}</span></div>
                <div className="lv-card-row"><span className="lv-card-label">Dates</span><span className="date-range">{fmtDate(l.start_date)} – {fmtDate(l.end_date)}</span></div>
                <div className="lv-card-row"><span className="lv-card-label">Days</span><span>{dayCount(l.start_date,l.end_date)}</span></div>
                <div className="lv-card-row" style={{flexDirection:'column',alignItems:'flex-start',gap:4}}>
                  <span className="lv-card-label">Reason</span>
                  <span className="reason-text">{l.reason||'—'}</span>
                </div>
                {l.status==='pending' && (
                  <div className="lv-card-actions">
                    <button className="act-btn approve" disabled={busyId===l._id} onClick={()=>approve(l)}>✓ Approve</button>
                    <button className="act-btn reject"  disabled={busyId===l._id} onClick={()=>openReject(l)}>✗ Reject</button>
                  </div>
                )}
                {l.status!=='pending' && l.reviewedBy && (
                  <div className="reviewed-meta" style={{marginTop:8}}>Reviewed by <b>{l.reviewedBy.name}</b>{l.review_note?` — "${l.review_note}"`:''}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {rejectFor && createPortal(
        <div className="mb" onClick={()=>setRejectFor(null)}>
          <div className="mo" onClick={e=>e.stopPropagation()}>
            <div className="mo-title">Reject leave for {rejectFor.student?.user?.name}?</div>
            <textarea className="mo-textarea" required placeholder="Reason for rejection" value={rejectNote} onChange={e=>setRejectNote(e.target.value)} />
            <div className="mo-ft">
              <button className="btn-ghost" onClick={()=>setRejectFor(null)}>Cancel</button>
              <button className="act-btn reject" disabled={busyId===rejectFor._id} onClick={confirmReject}>
                {busyId===rejectFor._id?'Rejecting…':'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      <ConfirmModal
        open={!!approveFor}
        title="Approve Leave"
        subtitle={approveFor?.student?.user?.name}
        message={approveFor ? `Approve leave for ${approveFor.student?.user?.name}? This will mark attendance as "On Leave" for ${dayCount(approveFor.start_date, approveFor.end_date)} day(s).` : ''}
        confirmLabel="Approve"
        danger={false}
        onConfirm={confirmApprove}
        onCancel={() => setApproveFor(null)}
        loading={busyId === approveFor?._id}
      />
    </div>
  );
}